/**
 * What the save control's script asks `/user/ci/words/`, and what comes back.
 *
 * Apart from `save-word.ts`, which is the page and the presses on it. This half
 * is the two requests and the shape of an answer, and it is separate because a
 * browser has to treat what it fetched as unknown until it has looked. Every
 * field below is read one at a time rather than trusted.
 *
 * The route is the handler's `CiWords`, and `src/user/ci-words.ts` says why it
 * answers JSON rather than a page.
 */

/** Where a reader's words live, and where a press without a script goes. */
export const ciPath = "/user/ci/";

/** Where the script asks about words and posts one. */
const ciWordsPath = "/user/ci/words/";

/** The field carrying the headword, on the form and on the query string. */
export const wordField = "word";

/** The optional tag carried by a save from the segmenter. */
export const tagField = "tag";

/** The parameter carrying the words a page is asking about. */
const wordsParameter = "words";

/** What separates them, which a headword can never carry. */
const wordsSeparator = ",";

/** DynamoDB's maximum number of keys in one BatchGetItem request. */
const wordsPerRequest = 100;

/** Keep a long segmenter's requests from opening every connection at once. */
const requestsAtOnce = 4;

/** The parameter naming a word the cap would not let through. */
const refusedParameter = "full";

/** What the route answers, before anything has looked at it. */
interface WordsAnswer {
  readonly saved?: unknown;
  readonly full?: unknown;
}

/** What one press did, as the page reads it. */
export interface PressResult {
  /** Whether the reader now holds the word. */
  readonly held: boolean;

  /** Whether the reader has no session, and is to be offered an account. */
  readonly signedOut: boolean;

  /** Whether the save was refused at the cap. */
  readonly full: boolean;
}

/** Where a reader is sent when the cap would not let a word through. */
export function refusedLocation(word: string): string {
  return `${ciPath}?${refusedParameter}=${encodeURIComponent(word)}`;
}

/**
 * Which of the words on this page the reader holds.
 *
 * Nothing where the answer is not a list of words, which is a reader with no
 * session and anything else this could not read. The page draws every control
 * offering to save, so an answer nobody understood leaves it as it is.
 */
export async function askHeldWords(
  fetched: typeof fetch,
  words: readonly string[],
): Promise<readonly string[] | undefined> {
  const distinct = [...new Set(words)];
  const batches = Array.from(
    { length: Math.ceil(distinct.length / wordsPerRequest) },
    (_, at) => distinct.slice(at * wordsPerRequest, (at + 1) * wordsPerRequest),
  );
  const held: string[] = [];
  let next = 0;

  const ask = async (): Promise<boolean> => {
    while (next < batches.length) {
      // The loop bound proves this index is present.
      const batch = batches[next] as readonly string[];
      next += 1;

      // oxlint-disable-next-line no-await-in-loop -- each worker keeps at most one request open and takes the next batch when it finishes
      const answer = await fetched(
        `${ciWordsPath}?${wordsParameter}=${encodeURIComponent(batch.join(wordsSeparator))}`,
      );
      // oxlint-disable-next-line no-await-in-loop -- the response body belongs to the request immediately above
      const saved = ((await answer.json()) as WordsAnswer).saved;

      if (!Array.isArray(saved)) {
        return false;
      }

      held.push(...(saved as readonly string[]));
    }

    return true;
  };

  const understood = await Promise.all(
    Array.from({ length: Math.min(requestsAtOnce, batches.length) }, ask),
  );

  return understood.every(Boolean) ? held : undefined;
}

/** Turn one word over, and say what happened to it. */
export async function toggleSavedWord(
  fetched: typeof fetch,
  word: string,
  tag?: string,
): Promise<PressResult> {
  const fields = new URLSearchParams({ [wordField]: word });
  if (tag !== undefined) {
    fields.set(tagField, tag);
  }

  const answer = await fetched(ciWordsPath, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: fields.toString(),
  });

  const reported = (await answer.json()) as WordsAnswer;
  const saved = reported.saved;

  return {
    held: Array.isArray(saved) && saved.includes(word),
    signedOut: !Array.isArray(saved),
    full: reported.full === true,
  };
}
